import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  useMarkNotificationRead,
  useNotifications,
} from "@src/packages/care/useNotifications";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  parseBadgeUnlockEvent,
  preferEarlierBadgeUnlockEvent,
  type BadgeUnlockEventEnvelope,
} from "./badge-events";

type PendingBadgeEvent = {
  event: BadgeUnlockEventEnvelope["event"];
  notificationIds: string[];
};

type BadgeCelebrationContextValue = {
  activeEvent: PendingBadgeEvent | null;
  /**
   * Everything unacknowledged, oldest first.
   *
   * The provider used to hand out only the first, which is why several badges
   * became several dialogs in a row. Exposing the whole set lets one dialog
   * show them together; `activeEvent` stays for the single-badge path and is
   * simply the first of these.
   */
  pendingEvents: PendingBadgeEvent[];
  dismissActive: () => void;
  /** Acknowledge every badge currently pending, in one gesture. */
  dismissAll: () => void;
};

const BadgeCelebrationContext = createContext<BadgeCelebrationContextValue>({
  activeEvent: null,
  pendingEvents: [],
  dismissActive: () => {},
  dismissAll: () => {},
});

export function BadgeCelebrationProvider({
  children,
  isBadgeSupported,
}: {
  children: ReactNode;
  isBadgeSupported: (badgeId: string) => boolean;
}) {
  const { data: notifications = [] } = useNotifications();
  const { patientId } = useApiClient();
  const markRead = useMarkNotificationRead();
  const [dismissedBadgeIds, setDismissedBadgeIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    setDismissedBadgeIds(new Set());
  }, [patientId]);

  const pendingEvents = useMemo(() => {
    // Badge unlocks are write-once. A replay can carry a new event id for the
    // same badge, so group by badge and keep the earliest event while still
    // acknowledging every unread notification for that badge together.
    const grouped = new Map<string, PendingBadgeEvent>();

    for (const notification of notifications) {
      if (notification.readAt) continue;

      const envelope = parseBadgeUnlockEvent(notification);
      if (
        !envelope ||
        !isBadgeSupported(envelope.event.badgeId) ||
        dismissedBadgeIds.has(envelope.event.badgeId)
      ) {
        continue;
      }

      const existing = grouped.get(envelope.event.badgeId);
      if (existing) {
        existing.notificationIds.push(notification.id);
        existing.event = preferEarlierBadgeUnlockEvent(
          existing.event,
          envelope.event,
        );
      } else {
        grouped.set(envelope.event.badgeId, {
          event: envelope.event,
          notificationIds: [notification.id],
        });
      }
    }

    return [...grouped.values()].sort(
      (a, b) => Date.parse(a.event.occurredAt) - Date.parse(b.event.occurredAt),
    );
  }, [dismissedBadgeIds, isBadgeSupported, notifications]);

  const dismiss = useCallback(
    (events: PendingBadgeEvent[]) => {
      if (events.length === 0) return;

      setDismissedBadgeIds((current) => {
        const next = new Set(current);
        for (const pending of events) next.add(pending.event.badgeId);
        return next;
      });

      for (const pending of events) {
        for (const notificationId of pending.notificationIds) {
          markRead.mutate(notificationId, {
            onError: () => {
              // Keep a failed acknowledgement available for retry, and only the
              // one that failed: dismissing four badges must not put the other
              // three back because one request lost the network. The server's
              // unread state remains the source of truth across reloads.
              setDismissedBadgeIds((current) => {
                const next = new Set(current);
                next.delete(pending.event.badgeId);
                return next;
              });
            },
          });
        }
      }
    },
    [markRead],
  );

  const dismissActive = useCallback(() => {
    const active = pendingEvents[0];
    if (active) dismiss([active]);
  }, [dismiss, pendingEvents]);

  const dismissAll = useCallback(() => {
    dismiss(pendingEvents);
  }, [dismiss, pendingEvents]);

  const value = useMemo(
    () => ({
      activeEvent: pendingEvents[0] ?? null,
      pendingEvents,
      dismissActive,
      dismissAll,
    }),
    [dismissActive, dismissAll, pendingEvents],
  );

  return (
    <BadgeCelebrationContext.Provider value={value}>
      {children}
    </BadgeCelebrationContext.Provider>
  );
}

export function useBadgeCelebration() {
  return useContext(BadgeCelebrationContext);
}
